"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useGachaSolanaConfig } from "./use-gacha-solana-config"

export const GACHA_USDC_BALANCE_REFRESH_EVENT = "gacha:usdc-balance-refresh"
const GACHA_USDC_BALANCE_REFRESH_DEBOUNCE_MS = 500
const GACHA_USDC_BALANCE_CONFIRMATION_REFRESH_MS = 5000
const GACHA_USDC_BALANCE_RECONCILIATION_DELAYS_MS = [750, 1250]
const GACHA_USDC_BALANCE_EPSILON = 0.000001

type GachaUsdcBalanceRefreshDetail = {
  walletAddress?: string
  balanceDelta?: number
}

type PendingGachaUsdcBalance = {
  expected: number
  direction: -1 | 1
}

export function requestGachaUsdcBalanceRefresh(
  walletAddress?: string,
  balanceDelta?: number,
) {
  window.dispatchEvent(
    new CustomEvent(GACHA_USDC_BALANCE_REFRESH_EVENT, {
      detail:
        walletAddress || Number.isFinite(balanceDelta)
          ? { walletAddress, balanceDelta }
          : undefined,
    }),
  )
}

export function applyGachaUsdcBalanceDelta(
  currentBalance: number | null,
  balanceDelta: number | undefined,
) {
  if (currentBalance === null || !Number.isFinite(balanceDelta)) {
    return currentBalance
  }

  const nextBalance = currentBalance + (balanceDelta ?? 0)
  return Math.max(0, Math.round(nextBalance * 1_000_000) / 1_000_000)
}

export function useGachaUsdcBalance(walletAddress: string | undefined) {
  const { rpcUrl, usdcMint } = useGachaSolanaConfig()
  const [balance, setBalance] = useState(walletAddress ? "-" : "0")
  const [balanceAmount, setBalanceAmount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(Boolean(walletAddress))
  const abortControllerRef = useRef<AbortController | null>(null)
  const refreshTimeoutRef = useRef<number | null>(null)
  const confirmationRefreshTimeoutRef = useRef<number | null>(null)
  const balanceAmountRef = useRef<number | null>(null)
  const pendingBalanceRef = useRef<PendingGachaUsdcBalance | null>(null)
  const reconciliationGenerationRef = useRef(0)

  const fetchBalance = useCallback(
    async ({
      showLoading = false,
      forceReconciliation = false,
    }: {
      showLoading?: boolean
      forceReconciliation?: boolean
    } = {}) => {
      if (!walletAddress) {
        balanceAmountRef.current = null
        pendingBalanceRef.current = null
        setBalance("0")
        setBalanceAmount(null)
        setIsLoading(false)
        return true
      }

      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      if (showLoading) {
        setIsLoading(true)
      }

      try {
        const response = await fetch(rpcUrl, {
          method: "POST",
          headers: {
            "content-type": "application/json",
          },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: "gacha-usdc-balance",
            method: "getTokenAccountsByOwner",
            params: [
              walletAddress,
              { mint: usdcMint },
              { encoding: "jsonParsed" },
            ],
          }),
          signal: controller.signal,
        })
        if (!response.ok) {
          throw new Error(`USDC balance RPC failed with ${response.status}`)
        }

        const payload = (await response.json()) as {
          error?: {
            code?: number
            message?: string
          }
          result?: {
            value?: Array<{
              account?: {
                data?: {
                  parsed?: {
                    info?: {
                      tokenAmount?: {
                        uiAmountString?: string
                      }
                    }
                  }
                }
              }
            }>
          }
        }

        if (payload.error) {
          throw new Error(
            payload.error.message ||
              `USDC balance RPC error ${payload.error.code ?? ""}`.trim(),
          )
        }

        if (!Array.isArray(payload.result?.value)) {
          throw new Error("USDC balance RPC response is missing token accounts")
        }

        const total = payload.result.value.reduce((sum, token) => {
          const amount = Number(
            token.account?.data?.parsed?.info?.tokenAmount?.uiAmountString || 0,
          )

          return Number.isFinite(amount) ? sum + amount : sum
        }, 0)

        if (controller.signal.aborted) {
          return false
        }

        const pendingBalance = pendingBalanceRef.current
        if (pendingBalance && !forceReconciliation) {
          const chainStateIsStale =
            pendingBalance.direction < 0
              ? total > pendingBalance.expected + GACHA_USDC_BALANCE_EPSILON
              : total < pendingBalance.expected - GACHA_USDC_BALANCE_EPSILON

          if (chainStateIsStale) {
            return false
          }
        }

        pendingBalanceRef.current = null
        balanceAmountRef.current = total
        setBalanceAmount(total)
        setBalance(formatGachaUsdcBalance(total))
        return true
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn("[Gacha] Failed to fetch USDC balance", error)
        }
        return false
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [rpcUrl, usdcMint, walletAddress],
  )

  useEffect(() => {
    if (!walletAddress) {
      balanceAmountRef.current = null
      pendingBalanceRef.current = null
      setBalance("0")
      setBalanceAmount(null)
      setIsLoading(false)
      return
    }

    const refreshAfterBalanceChange = (event: Event) => {
      const detail =
        event instanceof CustomEvent
          ? (event.detail as GachaUsdcBalanceRefreshDetail | undefined)
          : undefined
      const refreshWalletAddress =
        typeof detail?.walletAddress === "string"
          ? detail.walletAddress
          : undefined

      if (
        refreshWalletAddress &&
        refreshWalletAddress.toLowerCase() !== walletAddress.toLowerCase()
      ) {
        return
      }

      abortControllerRef.current?.abort()
      const reconciliationGeneration = reconciliationGenerationRef.current + 1
      reconciliationGenerationRef.current = reconciliationGeneration

      if (confirmationRefreshTimeoutRef.current !== null) {
        window.clearTimeout(confirmationRefreshTimeoutRef.current)
        confirmationRefreshTimeoutRef.current = null
      }

      const balanceDelta = detail?.balanceDelta
      const optimisticBalance = applyGachaUsdcBalanceDelta(
        balanceAmountRef.current,
        balanceDelta,
      )
      const hasOptimisticUpdate =
        optimisticBalance !== null && Number.isFinite(balanceDelta)

      if (hasOptimisticUpdate) {
        balanceAmountRef.current = optimisticBalance
        pendingBalanceRef.current = {
          expected: optimisticBalance,
          direction: (balanceDelta ?? 0) < 0 ? -1 : 1,
        }
        setBalanceAmount(optimisticBalance)
        setBalance(formatGachaUsdcBalance(optimisticBalance))

        const reconcile = (attempt: number) => {
          if (
            reconciliationGenerationRef.current !== reconciliationGeneration
          ) {
            return
          }

          const isLastAttempt =
            attempt === GACHA_USDC_BALANCE_RECONCILIATION_DELAYS_MS.length - 1
          confirmationRefreshTimeoutRef.current = window.setTimeout(
            async () => {
              confirmationRefreshTimeoutRef.current = null
              const reconciled = await fetchBalance({
                forceReconciliation: isLastAttempt,
              })
              if (
                reconciliationGenerationRef.current ===
                  reconciliationGeneration &&
                !reconciled &&
                !isLastAttempt
              ) {
                reconcile(attempt + 1)
              }
            },
            GACHA_USDC_BALANCE_RECONCILIATION_DELAYS_MS[attempt],
          )
        }

        reconcile(0)
        return
      }

      pendingBalanceRef.current = null
      fetchBalance()
      confirmationRefreshTimeoutRef.current = window.setTimeout(() => {
        confirmationRefreshTimeoutRef.current = null
        fetchBalance()
      }, GACHA_USDC_BALANCE_CONFIRMATION_REFRESH_MS)
    }
    const scheduleRefresh = () => {
      if (document.visibilityState !== "visible") {
        return
      }

      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current)
      }

      refreshTimeoutRef.current = window.setTimeout(() => {
        refreshTimeoutRef.current = null
        fetchBalance()
      }, GACHA_USDC_BALANCE_REFRESH_DEBOUNCE_MS)
    }

    setBalance("-")
    balanceAmountRef.current = null
    pendingBalanceRef.current = null
    setBalanceAmount(null)
    fetchBalance({ showLoading: true })
    window.addEventListener("focus", scheduleRefresh)
    window.addEventListener(
      GACHA_USDC_BALANCE_REFRESH_EVENT,
      refreshAfterBalanceChange,
    )
    document.addEventListener("visibilitychange", scheduleRefresh)

    return () => {
      reconciliationGenerationRef.current += 1
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
      if (confirmationRefreshTimeoutRef.current !== null) {
        window.clearTimeout(confirmationRefreshTimeoutRef.current)
        confirmationRefreshTimeoutRef.current = null
      }
      window.removeEventListener("focus", scheduleRefresh)
      window.removeEventListener(
        GACHA_USDC_BALANCE_REFRESH_EVENT,
        refreshAfterBalanceChange,
      )
      document.removeEventListener("visibilitychange", scheduleRefresh)
      abortControllerRef.current?.abort()
    }
  }, [fetchBalance, walletAddress])

  return { balance, balanceAmount, isLoading }
}

export function formatGachaUsdcBalance(balance: number) {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(balance)
}
